import { useEffect, useMemo, useState } from "react";
import { confirm } from "../components/ConfirmDialog";
import { apiClient } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Lightbulb, Rocket, Trash2, Pencil, ChevronDown, Users, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  scheduling:    "Scheduling",
  payroll:       "Payroll",
  time_entry:    "Time Entry",
  reporting:     "Reporting",
  employees:     "Employees",
  notifications: "Notifications",
  integrations:  "Integrations",
  other:         "Other",
};

const STATUS_META = {
  submitted: { label: "Submitted", color: "bg-slate-100 text-slate-700" },
  in_review: { label: "In Review", color: "bg-amber-50 text-amber-700" },
  planned:   { label: "Planned",   color: "bg-blue-50 text-blue-700" },
  shipped:   { label: "Shipped",   color: "bg-emerald-50 text-emerald-700" },
  declined:  { label: "Declined",  color: "bg-slate-100 text-slate-500 line-through" },
};

const PRIORITY_LABELS = {
  nice_to_have: "Nice to have",
  important:    "Important",
  must_have:    "Must have",
};

export default function AdminFeatureRequests() {
  const [payload, setPayload] = useState({ ideas: [], total_ideas: 0, total_submissions: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchIdeas = async () => {
    try {
      const res = await apiClient.get("/admin/feature-ideas");
      setPayload(res.data || { ideas: [], total_ideas: 0, total_submissions: 0, categories: [] });
    } catch {
      toast.error("Failed to load feature ideas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIdeas(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payload.ideas.filter((i) => {
      if (statusFilter !== "all" && (i.status || "submitted") !== statusFilter) return false;
      if (categoryFilter !== "all" && (i.category || "other") !== categoryFilter) return false;
      if (!q) return true;
      const hay = `${i.title} ${i.summary}`.toLowerCase();
      return hay.includes(q);
    });
  }, [payload.ideas, search, statusFilter, categoryFilter]);

  const patch = async (ideaId, body) => {
    try {
      await apiClient.patch(`/admin/feature-ideas/${ideaId}`, body);
      toast.success("Idea updated");
      fetchIdeas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (idea) => {
    const ok = await confirm({
      title: `Delete "${idea.title}"?`,
      description: `This removes ${idea.submitter_count} tenant submission(s) tied to this idea. Voting history is preserved in the audit log but tenants will no longer see the idea in their portal.`,
      confirmText: "Delete idea",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/admin/feature-ideas/${idea.idea_id}`);
      toast.success("Idea deleted");
      fetchIdeas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const promote = async (idea) => {
    try {
      await apiClient.post(`/admin/feature-ideas/${idea.idea_id}/promote-to-roadmap`);
      toast.success("Promoted to roadmap");
      fetchIdeas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Promotion failed");
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl space-y-6" data-testid="admin-feature-ideas-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-amber-500" /> Customer Ideas
          </h1>
          <p className="text-slate-600 mt-1">
            AI-deduplicated ideas from every tenant. Sorted by demand (most-requested first).
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ideas</p>
            <p className="text-3xl font-bold text-[#0A192F]">{payload.total_ideas}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Submissions</p>
            <p className="text-3xl font-bold text-[#0A192F]">{payload.total_submissions}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Planned</p>
            <p className="text-3xl font-bold text-blue-700">
              {payload.ideas.filter((i) => i.status === "planned").length}
            </p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Shipped</p>
            <p className="text-3xl font-bold text-emerald-700">
              {payload.ideas.filter((i) => i.status === "shipped").length}
            </p>
          </CardContent></Card>
        </div>

        <Card>
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle className="text-lg">All Ideas</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Input
                data-testid="feature-ideas-search"
                placeholder="Search title/summary…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-56"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="feature-ideas-status-filter" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="feature-ideas-category-filter" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {payload.categories.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-slate-500 py-12">
                No ideas match these filters.
              </div>
            ) : (
              <div className="space-y-2" data-testid="feature-ideas-list">
                {filtered.map((idea) => {
                  const st = STATUS_META[idea.status] || STATUS_META.submitted;
                  const isOpen = expandedId === idea.idea_id;
                  return (
                    <div
                      key={idea.idea_id}
                      data-testid={`feature-idea-${idea.idea_id}`}
                      className="border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : idea.idea_id)}
                        className="w-full text-left p-4 flex items-start gap-3"
                      >
                        <div
                          className="shrink-0 w-16 h-16 flex flex-col items-center justify-center rounded-lg bg-amber-50 border border-amber-200"
                          data-testid={`feature-idea-count-${idea.idea_id}`}
                          title={`${idea.submitter_count} tenant(s) asked`}
                        >
                          <span className="text-2xl font-bold text-amber-700 leading-none">{idea.submitter_count}</span>
                          <span className="text-[9px] uppercase tracking-wider text-amber-600 font-semibold mt-1">tenants</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h4 className="font-semibold text-[#0A192F] break-words">{idea.title}</h4>
                            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${st.color}`}>
                              {st.label}
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                              {CATEGORY_LABELS[idea.category] || idea.category}
                            </span>
                            {idea.linked_roadmap_item_id && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                On roadmap
                              </span>
                            )}
                            {idea.llm_error_on_intake && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-50 text-rose-700 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> AI failed
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 leading-snug">{idea.summary}</p>
                          <p className="text-[11px] text-slate-400 mt-1">
                            Last updated {new Date(idea.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>

                      {isOpen && (
                        <div className="border-t border-slate-100 p-4 space-y-4">
                          <div className="flex flex-wrap gap-2">
                            <Select value={idea.status || "submitted"} onValueChange={(v) => patch(idea.idea_id, { status: v })}>
                              <SelectTrigger className="w-40" data-testid={`feature-idea-status-${idea.idea_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_META).map(([k, m]) => (
                                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={idea.category || "other"} onValueChange={(v) => patch(idea.idea_id, { category: v })}>
                              <SelectTrigger className="w-44" data-testid={`feature-idea-category-${idea.idea_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {payload.categories.map((c) => (
                                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="outline" onClick={() => setEditing(idea)}
                              data-testid={`feature-idea-edit-${idea.idea_id}`}>
                              <Pencil className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            {!idea.linked_roadmap_item_id && (
                              <Button size="sm" onClick={() => promote(idea)}
                                data-testid={`feature-idea-promote-${idea.idea_id}`}
                                className="bg-[#0D9488] hover:bg-[#14B8A6]">
                                <Rocket className="w-4 h-4 mr-1" /> Promote to Roadmap
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => remove(idea)}
                              data-testid={`feature-idea-delete-${idea.idea_id}`}
                              className="text-rose-500 hover:text-rose-700">
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>

                          <div>
                            <h5 className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-2">
                              <Users className="w-3.5 h-3.5" /> Individual submissions
                            </h5>
                            <div className="space-y-2">
                              {(idea.submissions || []).map((s, idx) => (
                                <div key={idx} className="p-3 rounded border border-slate-100 bg-slate-50 text-sm">
                                  <p className="text-slate-500 text-xs">
                                    <strong className="text-slate-700">{s.submitted_by_name}</strong>
                                    {" · "}
                                    <span className="font-mono">{s.tenant_id}</span>
                                    {" · "}
                                    <span>{PRIORITY_LABELS[s.priority] || s.priority}</span>
                                    {" · "}
                                    {new Date(s.submitted_at).toLocaleDateString()}
                                  </p>
                                  <p className="mt-1 font-medium text-[#0A192F]">{s.raw_title}</p>
                                  {s.raw_description && (
                                    <p className="mt-1 text-slate-600 whitespace-pre-line">{s.raw_description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="feature-idea-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Idea</DialogTitle>
            <DialogDescription>
              Refine the AI-generated title and summary before promoting to the roadmap.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await patch(editing.idea_id, {
                  title: editing.title,
                  summary: editing.summary,
                });
                setEditing(null);
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="ei-title">Title</Label>
                <Input id="ei-title" data-testid="feature-idea-edit-title" required maxLength={140}
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ei-summary">Summary</Label>
                <Textarea id="ei-summary" data-testid="feature-idea-edit-summary" rows={3} maxLength={280}
                  value={editing.summary || ""}
                  onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" className="bg-[#0D9488] hover:bg-[#14B8A6]"
                  data-testid="feature-idea-edit-save">Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
